import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { ShowBillingHold } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { formatCpmCountdown, formatUsername, holdRemainingMs } from '@fetmode/utils';

export interface BillingHoldBannerProps {
  holds: ShowBillingHold[];
}

export function BillingHoldBanner({ holds }: BillingHoldBannerProps): ReactElement | null {
  const [now, setNow] = useState(() => Date.now());
  const active = holds.filter((hold) => holdRemainingMs(hold.holdUntil, now) > 0);

  useEffect(() => {
    if (holds.length === 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [holds.length]);

  if (active.length === 0) {
    return null;
  }

  return (
    <div
      className="live-controls__hold-banner"
      role="status"
      aria-live="polite"
      style={{
        marginBottom: '0.75rem',
        padding: '0.75rem 0.9rem',
        borderRadius: '0.5rem',
        border: '1px solid color-mix(in srgb, var(--fm-color-secondary) 45%, var(--fm-color-border))',
        background: 'color-mix(in srgb, var(--fm-color-primary) 18%, var(--fm-color-surface))',
      }}
    >
      <p style={{ margin: 0, fontWeight: 650 }}>{t('common.studio.live.billingHoldBanner')}</p>
      <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
        {active.map((hold) => (
          <li key={hold.userId} style={{ fontSize: '0.875rem' }}>
            {t('common.studio.live.billingHoldMember')
              .replace('{{username}}', formatUsername(hold.username))
              .replace('{{time}}', formatCpmCountdown(holdRemainingMs(hold.holdUntil, now)))}
          </li>
        ))}
      </ul>
    </div>
  );
}
